import {
  CreateAccountOkResponse,
  CreateAccountRequest,
} from 'api/ErplySignupApi.types';
import ErplySignupApi from 'api/ErplySignupApi';
import { PricePlanFormContent } from 'app/pages/Registration/interfaces';
import baseForm from 'assets/baseForm';
import { CountriesKeys, supportedCountries } from 'service/countrisList';
import { IForm } from 'app/pages/Registration/utils';

import { getHardwareNeeds, getPrice } from './utils';

export default class ErplySignupService {
  static async createAccount({
    email: { email },
    contactPerson: { firstName, lastName, phone, role },
    pricePlan: { plan, period },
    password: { password },
    desktopHardwareQuestions: { needsDesktopHardware, ...desktopHardware },
    mobileHardwareQuestions: { needsMobileHardware, ...tabletHardware },
    companyLocations: { numberOfLocations },
    companySize: {
      numberOfProducts,
      numberOfSystemUsers,
      numberOfCashRegisters,
    },
    softwareQuestions: {
      retailManagementSoftware,
      warehouseManagement,
      stockTake,
      webshopConnection,
      pointOfSale,
    },
    companyDetails: {
      vatNumber,
      addressLine1,
      addressLine2,
      regCode,
      companyName,
      postCode,
    },
    region: { countrySelect },
  }: IForm): Promise<CreateAccountOkResponse | void> {
    const pricePlans = (baseForm
      .find((el) => el.path === 'pricePlan')
      ?.content.find((el) => el.type === 'pricePlan') ??
      []) as PricePlanFormContent;
    const pricePlan = pricePlans.options[(plan as number) ?? 0];
    const price = getPrice({
      month: pricePlan.monthPrice,
      locations: numberOfLocations,
      period: period as 'monthly' | 'annual',
      year: pricePlan.monthPrice,
    });
    const data: CreateAccountRequest = {
      assignmentComment: '',

      companyName: companyName as string,
      firstName: firstName as string,
      lastName: lastName as string,
      email: email as string,
      phone: phone as string,
      username: email as string,
      password: password as string,

      sendEmail: '1',

      countryCode: (countrySelect as string) ?? '',
      addressCountry: supportedCountries[countrySelect as CountriesKeys].name,
      addressLine1: (addressLine1 as string) ?? '',
      addressLine2: (addressLine2 as string) ?? '',
      addressPostCode: (postCode as string) ?? '',

      planName: pricePlan?.title ?? '',
      planPrice: price.toString(),

      notes: `Contact person details
- First name: ${firstName}
- Last name: ${lastName}
${role !== undefined && `- Role: ${role}`}
- Email: ${email}
- Phone: ${phone}

Company Details
- Company Name: ${companyName}
- Reg code: ${regCode}
- Address Line 1: ${addressLine1}
- Address Line 2: ${addressLine2}
- Vat Number: ${vatNumber}
- Number of locations: ${numberOfLocations}
- Number of Cash Registers: ${numberOfCashRegisters}
- Number of System Users: ${numberOfSystemUsers}
- Number of Products: ${numberOfProducts}

Software Needs
- Retail management software: ${retailManagementSoftware}
- Point of sale: ${pointOfSale}
- Warehouse management: ${warehouseManagement}
- Stocktake: ${stockTake}
- Webshop connection: Woocommerce, Magento, Shopify, Wix, etc.: ${webshopConnection}

Hardware Needs
  Mobile: ${needsMobileHardware}
${(needsMobileHardware as boolean) && getHardwareNeeds(tabletHardware)}
  Desktop: ${needsDesktopHardware}
${(needsDesktopHardware as boolean) && getHardwareNeeds(desktopHardware)}

Selected Pricing Plan
- Price: ${price}
- Billing: ${period}`,
    };
    return ErplySignupApi.createAccount(data);
  }
}
